import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResultDefaultError } from '../../../api/request';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import DisplayInfo from '../../../components/info/DisplayInfo';
import Loading from '../../../components/info/Loading';
import { TranslationsKeys } from '../../../locales/translations';
import { getCreditLiquidationPreview } from '../api';
import { CreditLiquidationFilter, LiquidationPreviewResponse } from '../types';

interface CreditLiquidationDataLoaderProps {
  children: ReactElement[];
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  CreditLiquidationFilter,
  LiquidationPreviewResponse,
  AxiosResultDefaultError
>();

export function useCreditLiquidationDataLoader() {
  return useFetch();
}

export const CreditLiquidationDataLoaderConsumer = FetchConsumer;

export default function CreditLiquidationDataLoader({
  children,
}: CreditLiquidationDataLoaderProps) {
  const { t } = useTranslation(TranslationsKeys.CREDIT_LIQUIDATION);
  const request = useCallback(async (query: CreditLiquidationFilter) => {
    return getCreditLiquidationPreview({
      ...query,
      items_per_page: 10,
      page: query.page - 1,
    });
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1, items_per_page: 10, periodId: 0 }}
      fetchImmediately={false}
    >
      {children[0]}
      <FetchConsumer>
        {({ loading, data, error, refresh, query }) => {
          if (loading && query.periodId) {
            return <Loading insideCard />;
          } else if (error) {
            return (
              <DisplayError
                insideCard
                title={error.status === 404 ? t('error.title') : undefined}
                textBody={error.status === 404 ? t('error.body') : error.code}
                retryAction={refresh}
              />
            );
          } else if (data) {
            return children[1];
          } else {
            return (
              <DisplayInfo
                insideCard
                title={t('table.searchTitle')}
                textBody={t('table.searchDescription')}
              />
            );
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
